import { computed, watch } from 'vue'

import type { RabbitEventMessage } from '@collector/queue-gateway-shared-types'
import { formatDateInTimezone } from '@collector/shared-utils'
import type {
  Event,
  Participant,
  Stat,
  StatId,
} from '@collector/sportsapi-client-legacy'
import type { SubParticipant } from '@collector/sportsapi-types'
import { queryClient } from '@desktop/queryClient'
import { createSyncedTimestamp } from '@desktop/views/Relation/utils'

import {
  CompetitionData,
  updateCompetitionEvent,
  useCompetitionQuery,
} from './queries/useCompetitionQuery'
import { useQueueClient } from './queue/useQueueClient'
import { useEventId } from './useEventId'

const deepMerge = (a: object, b: object): object => {
  const out: Record<string, unknown> = { ...a }
  for (const [k, bv] of Object.entries(b)) {
    const av = out[k]
    out[k] =
      av &&
      bv &&
      typeof av === 'object' &&
      typeof bv === 'object' &&
      !Array.isArray(av) &&
      !Array.isArray(bv)
        ? deepMerge(av, bv)
        : bv
  }
  return out
}

/**
 * Event socket → vue-query writes.
 * Open this file to see exactly how cache data is transformed.
 */
export const writeEventMessage = (
  eventId: number,
  incoming: Event,
  ut: number,
) => {
  const competitionData = queryClient.getQueryData<CompetitionData>([
    'event',
    'competition',
    eventId,
  ])
  const prev = competitionData?.competition.season.stage.group.event

  // Patch event: spread incoming, fix start_date, merge participants by id.
  let event: Event
  if (!prev) {
    event = incoming
  } else {
    const byId = new Map((prev.participants ?? []).map((p) => [p.id, p]))
    event = {
      ...prev,
      ...incoming,
      start_date: incoming.start_date
        ? formatDateInTimezone(incoming.start_date + 'Z', 'yyyy-MM-dd HH:mm')
        : prev.start_date,
      participants: incoming.participants
        ? incoming.participants.map((p) => {
            const old = byId.get(p.id)
            return old ? (deepMerge(old, p) as Participant) : p
          })
        : prev.participants,
    }
  }

  queryClient.setQueryData<CompetitionData>(
    ['event', 'competition', eventId],
    (oldData) => {
      const updated = updateCompetitionEvent(oldData, () => event)
      if (!updated) return updated
      return {
        ...updated,
        competition: {
          ...updated.competition,
          timestamp: createSyncedTimestamp(ut),
        },
      }
    },
  )
  queryClient.setQueryData(
    ['event', 'participants', eventId],
    event.participants,
  )

  const cachedSubs =
    queryClient.getQueryData<SubParticipant[]>([
      'event',
      'subParticipants',
      eventId,
    ]) ?? []
  // Wire/event payload uses legacy SubParticipant (`id` may be null).
  const fromEvent = (incoming.participants ?? []).flatMap(
    (p) => p.subparticipants ?? [],
  )

  // Keep roster rows from cache; overlay stats/fields from event message.
  // Preserve old `id` — overwriting it breaks bulk stats update.
  queryClient.setQueryData(
    ['event', 'subParticipants', eventId],
    cachedSubs.map((old) => {
      const patch = fromEvent.find(
        (x) => x.id === old.id || x.short_name === old.short_name,
      )
      if (!patch || !old.stats) return old

      const statsById = (patch.stats ?? []).reduce(
        (acc, stat) => {
          acc[stat.id] = stat
          return acc
        },
        {} as Record<StatId, Stat>,
      )

      return {
        ...old,
        ...patch,
        id: old.id,
        stats: old.stats.map((s) => {
          const u = statsById[s.id]
          return u ? { ...s, ...u } : s
        }),
      }
    }),
  )
}

export const eventHandler = (
  eventId: number,
  messages: RabbitEventMessage[],
): void => {
  for (const message of messages) {
    writeEventMessage(eventId, message.data, message.ut)
  }
}

export const useCompetition = () => {
  const { data } = useCompetitionQuery()

  const competition = computed(() => data.value!.competition)

  return { competition }
}

export const useLiveCompetition = () => {
  const { connected, client } = useQueueClient()
  const eventId = useEventId()

  watch(
    connected,
    (isConnected, _, onCleanup) => {
      if (!isConnected) return
      onCleanup(
        client.subscribe(`events/${eventId.value}/event`, {
          sync: { method: 'latest' },
          onMessageBatch: (messages) => eventHandler(eventId.value, messages),
        }),
      )
    },
    { immediate: true },
  )
}
